<script setup>
    import { computed } from 'vue';
    import store from '@/store/global.store';
    import sessionService from '@/services/session.service';
    import Dropdown from '@/components/shared/ui/Dropdown';

    const $emit = defineEmits(['toggle-sidebar']);

    // Beta chrome: server-injected window.environment, beta-admin host, or Dev App Service host.
    // Never manage.greasebook.com / Production.
    const isBeta = computed(() => {
        const env = (window.environment || '').toLowerCase();
        if (env === 'beta' || env === 'development') return true;

        const host = window.location.hostname.toLowerCase();
        return host === 'beta-admin.greasebook.com'
            || host.startsWith('beta-admin.')
            || host.startsWith('dev-gsb-admin.');
    });

    const user = computed(() => {
        const currentUser = store.getters.currentUser();

        return {
            ...currentUser,
            name: currentUser?.userName?.split('@')?.[0]
        };
    });

    const toggleSidebar = () => $emit('toggle-sidebar');

    const logout = () => {
        sessionService.logout().finally(() => {
            sessionService.removeToken();
            location.href = '/auth/login';
        });
    };
</script>

<template>
    <header class="header" :class="{ 'header--beta': isBeta }">
        <button
            id="toogleMenuBtn"
            class="btn btn-outline-light d-lg-none"
            type="button"
            aria-label="Toggle navigation"
            @click="() => toggleSidebar()"
        >
            <i class="bi bi-list fs-4"></i>
        </button>

        <a href="/Home" class="d-flex align-items-center text-white text-decoration-none">
            <img src="/assets/images/logo.svg" height="38" />
            <div class="ms-2 lh-1">
                <div class="fs-3 mb-1">GreaseBook</div>
                <div class="d-flex align-items-center gap-1">
                    <small class="badge rounded-pill header-badge header-badge--admin">ADMIN PORTAL</small>
                    <small v-if="isBeta" class="badge rounded-pill header-badge header-badge--beta">BETA</small>
                </div>
            </div>
        </a>

        <div class="ms-auto d-flex align-items-center">
            <Dropdown buttonClass="fs-5 text-white" menuClass="p-0" menuStyle="min-width: 18rem" :noCaret="!user.name">
                <template #button>
                    <i class="bi bi-person-circle align-middle fs-4 me-1"></i>
                    <span class="d-none d-md-inline-block">{{ user.name }}</span>
                </template>
                <template #header>
                    <div class="dropdown-header border-bottom py-3">
                        <h6 class="mb-0 text-black">{{ user.fullName }}</h6>
                        <small>{{ user.userName }}</small>
                    </div>
                </template>
                <li @click="logout" class="dropdown-item d-flex align-items-center">
                    <i class="bi bi-box-arrow-in-left fs-3 me-1"></i>
                    <span>LOGOUT</span>
                </li>
            </Dropdown>
        </div>
    </header>
</template>

<style lang="scss">
    @import '@/styles/_gsb-variables.scss';

    .header {
        background-color: #212529;
        height: $header-height;
        position: fixed;
        top: 0;
        right: 0;
        left: 0;
        padding: 0 1rem;
        z-index: 1000;
        display: flex;
        align-items: center;
        gap: 1.5rem;

        .btn {
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }

            &:focus,
            &:active {
                outline: none !important;
                box-shadow: none;
            }
        }

        #toogleMenuBtn {
            height: 3rem;
            width: 3rem;
        }
    }

    /* Flat selectors: nested `&--` under `.header-badge` compiled to broken
       `.header --admin.header-badge` in the production CSS bundle. */
    .header.header--beta {
        background-color: #3a4f4c;
        border-bottom: 3px solid #2c8882;
    }

    .header-badge--admin {
        background: #f05840;
    }

    .header-badge--beta {
        background: #c62828;
        letter-spacing: 0.04em;
    }
</style>